import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Container, Row, Col, Button } from 'react-bootstrap';
import { 
  FaMicrochip, 
  FaLightbulb, 
  FaServer, 
  FaUsers,
  FaCheckCircle,
  FaArrowRight,
  FaCube,
  FaNetworkWired,
  FaCloud,
  FaBrain,
  FaCode
} from 'react-icons/fa';
import EditableText from '../components/EditableText';

const services = [
  {
    id: 'gpu-rental',
    title: 'GPU Rental',
    description: 'Access powerful NVIDIA H200 and Blackwell GPUs for your AI workloads. Flexible compute solutions tailored to your needs.',
    icon: <FaMicrochip />,
    features: [
      'Flexible hourly/monthly plans',
      'Single GPU to multi-node clusters',
      'High-performance RDMA networking',
      'Instant provisioning',
      '24/7 technical support'
    ],
    useCases: ['Machine learning training', 'Inference at scale', 'Research workloads', 'Model fine-tuning']
  },
  {
    id: 'kubernetes',
    title: 'Kubernetes as a Service',
    description: 'Fully managed Kubernetes clusters with GPU scheduling, auto-scaling, and enterprise-grade reliability.',
    icon: <FaCube />,
    features: [
      'GPU-aware scheduling',
      'Auto-scaling clusters',
      'Integrated monitoring & logging',
      'Enterprise SLAs (99.9%)',
      'Multi-node GPU pools',
      'Custom storage classes'
    ],
    useCases: ['ML model serving', 'Distributed training', 'AI microservices', 'Batch processing']
  },
  {
    id: 'baremetal',
    title: 'Bare Metal SXM',
    description: 'Dedicated NVIDIA SXM form factor servers with direct GPU-to-GPU connectivity for maximum performance.',
    icon: <FaServer />,
    features: [
      'NVIDIA HGX/HBM integration',
      'Dedicated tenant isolation',
      'Custom server configurations',
      'Ultra-low latency (<1ms)',
      'NVLink/NVSwitch support'
    ],
    useCases: ['Large-scale training', 'HPC workloads', 'Real-time inference', 'Research clusters']
  },
  {
    id: 'infiniband',
    title: 'InfiniBand Rail Topology',
    description: 'High-speed interconnect fabric optimized for distributed AI training and GPU clustering.',
    icon: <FaNetworkWired />,
    features: [
      '400Gb/s HDR InfiniBand',
      'Rail-optimized topology',
      'NVLink bridge support',
      'MPI & NCCL acceleration',
      'RDMA everywhere',
      'Top-of-rack redundancy'
    ],
    useCases: ['Distributed training', 'Multi-node inference', 'Parallel computing', 'AI factory clusters']
  },
  {
    id: 'iaac',
    title: 'IaC Support',
    description: 'Infrastructure as Code with support for automated provisioning.',
    icon: <FaCode />,
    features: [
      'Infrastructure templates',
      'Version-controlled configs',
      'CI/CD integration',
      'State management',
      'Multi-region deployment'
    ],
    useCases: ['Infrastructure automation', 'GitOps workflows', 'Multi-cloud setups', 'Reproducible environments']
  },
  {
    id: 'llm-ondemand',
    title: 'LLM on Demand',
    description: 'Ready-to-use Large Language Models with API access, fine-tuning, and optimized inference.',
    icon: <FaBrain />,
    features: [
      'Open-source model gallery',
      'Fine-tuning support',
      'Streaming API endpoints',
      'Pay-per-token pricing',
      'Custom model deployment',
      'RAG pipeline integration'
    ],
    useCases: ['Chatbots', 'Code generation', 'Document analysis', 'Virtual assistants']
  },
  {
    id: 'use-case-dev',
    title: 'Use Case Development Kit',
    description: 'Accelerate your AI journey with pre-built tools, frameworks, and expertise from NVIDIA and 6G AI Sweden.',
    icon: <FaLightbulb />,
    features: [
      'NVIDIA Blueprints',
      'Pre-trained Generative AI models',
      'SDKs and APIs',
      'Development frameworks',
      'Technical documentation'
    ],
    useCases: ['Computer vision', 'Natural language processing', 'RAG applications', 'AI agents']
  },
  {
    id: 'ai-solutions',
    title: 'AI Solutions',
    description: 'Turnkey AI applications ready for immediate business deployment across industries.',
    icon: <FaCloud />,
    features: [
      'Ready-to-deploy AI applications',
      'Industry-specific solutions',
      'Custom development options',
      'Integration support',
      'Ongoing maintenance'
    ],
    useCases: ['Healthcare AI', 'Financial services', 'Manufacturing', 'Retail analytics']
  },
  {
    id: 'advisory',
    title: 'Advisory Services',
    description: 'Expert guidance to help you identify and implement high-impact AI use cases in your organization.',
    icon: <FaUsers />,
    features: [
      'AI strategy consulting',
      'Use case identification',
      'Implementation roadmaps',
      'Operational optimization',
      'Team training'
    ],
    useCases: ['Digital transformation', 'Process automation', 'Data strategy', 'AI governance']
  }
];

function Services() {
  const [content, setContent] = useState({});

  useEffect(() => {
    fetch('/api/content?page=services')
      .then(res => res.json())
      .then(data => setContent(data))
      .catch(() => {});
  }, []);

  return (
    <>
      <Helmet>
        <title>AI Services | GPU Rental & Solutions | 6G AI Sweden</title>
        <meta name="description" content="Comprehensive AI cloud services including GPU rental, use case development, AI solutions, and advisory services. Powered by NVIDIA technology." />
        <meta name="keywords" content="GPU rental, AI solutions, use case development, AI advisory, machine learning services, NVIDIA AI" />
        <meta property="og:title" content="AI Services | 6G AI Sweden" />
        <meta property="og:description" content="Comprehensive AI cloud services powered by NVIDIA." />
      </Helmet>

      {/* Page Header */}
      <section className="page-header">
        <Container>
          <Row>
            <Col lg={8}>
              <h1 className="page-title"><EditableText section="page_title" page="services" defaultValue="AI Services" as="span" /></h1>
              <p className="page-subtitle">
                <EditableText section="page_subtitle" page="services" defaultValue="Comprehensive AI cloud services to accelerate your innovations. From GPU rental to full-scale AI solutions." as="span" />
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* Services Detail */}
      <section className="section">
        <Container>
          <Row>
            {services.map((service, index) => (
              <Col lg={6} key={service.id} className="mb-4">
                <div className="service-card service-card-large">
                  <div className="service-icon">
                    {service.icon}
                  </div>
                  <h2 className="service-detail-title"><EditableText section={`service_${service.id}_title`} page="services" defaultValue={service.title} as="span" /></h2>
                  <p className="service-detail-desc">
                    <EditableText section={`service_${service.id}_desc`} page="services" defaultValue={service.description} as="span" />
                  </p>
                  <Row>
                    <Col md={6}>
                      <h5 className="service-list-title">Features</h5>
                      <ul className="service-features">
                        {service.features.map((feature, idx) => (
                          <li key={idx}><EditableText section={`service_${service.id}_feature_${idx}`} page="services" defaultValue={feature} as="span" /></li>
                        ))}
                      </ul>
                    </Col>
                    <Col md={6}>
                      <h5 className="service-list-title">Use Cases</h5>
                      <ul className="service-features">
                        {service.useCases.map((useCase, idx) => (
                          <li key={idx}><EditableText section={`service_${service.id}_usecase_${idx}`} page="services" defaultValue={useCase} as="span" /></li>
                        ))}
                      </ul>
                    </Col>
                  </Row>
                </div>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* CTA */}
      <section className="section section-dark">
        <Container>
          <div className="cta-section">
            <h2 className="cta-title">Need Custom Solutions?</h2>
            <p className="cta-text">
              Our team can help design a tailored AI solution for your specific needs.
            </p>
            <Button variant="primary" size="lg" as={Link} to="/contact">
              Talk to Our Experts <FaArrowRight />
            </Button>
          </div>
        </Container>
      </section>
    </>
  );
}

export default Services;
